import { useState } from "react";
import type { MutableRefObject } from "react";
import { Loc, t } from "@/locales";
import { MODEL_ZOOM } from "@/shared/config/constants";
import type { Scene3D } from "@/configurator/utils/scene/Scene3D";

type Props = {
  sceneRef: MutableRefObject<Scene3D | null>;
};

/**
 * Stepped zoom for the 3D viewer. The wheel intentionally does not zoom (it must
 * keep scrolling the host page), so these buttons are the only zoom affordance.
 *
 * The step lives here rather than being read back from the scene because the
 * scene is recreated when the model changes; the parent remounts this component
 * with the model, which resets the step in step with the fresh framing.
 */
export function ZoomControls({ sceneRef }: Props) {
  const [step, setStep] = useState(0);

  const zoomBy = (delta: number) => {
    const scene = sceneRef.current;
    if (!scene) return;
    setStep(scene.setZoomStep(step + delta));
  };

  return (
    <div className="zoom-controls">
      <button
        type="button"
        className="zoom-controls__button"
        onClick={() => zoomBy(1)}
        disabled={step >= MODEL_ZOOM.IN_STEPS}
        aria-label={t(Loc.zoomIn)}
      >
        <span className="zoom-controls__sign" aria-hidden="true">
          +
        </span>
      </button>
      <button
        type="button"
        className="zoom-controls__button"
        onClick={() => zoomBy(-1)}
        disabled={step <= -MODEL_ZOOM.OUT_STEPS}
        aria-label={t(Loc.zoomOut)}
      >
        <span className="zoom-controls__sign" aria-hidden="true">
          −
        </span>
      </button>
    </div>
  );
}
